import { faro } from '@gate31/faro/src';
import Inputmask from 'inputmask';
import { Base } from '@gate31/core/src/libs/BaseComponent';
import { Gate31ApiV2 } from '@gate31/core/src/api/gate31-api';
import { validateEmail } from '@gate31/core/src/libs/utils';
import { CustomCn } from '@gate31/core/src/libs/classname';
import { ConfirmPhone } from '../ConfirmPhone/ConfirmPhone';
import { PasswordToggle } from '../PasswordToggle/PasswordToggle';
import { Pixel } from '../analitycs/pixel/pixel';

interface FieldError {
    fieldName: string;
    isEmpty: boolean;
}

export class CreateAccountForm extends Base<HTMLElement> {
    cn: CustomCn;
    source?: string

    form: HTMLFormElement;
    formButton: HTMLButtonElement;
    loader: HTMLDivElement;

    im: Inputmask.Instance;
    configPhone: ConfirmPhone;

    successHandle?: () => void

    constructor(options: {
        node: HTMLElement;
        source?: string;
        cn: CustomCn;
        successHandle?: () => void;
        configPhone: ConfirmPhone;
        passwordToggler?: PasswordToggle;
    }) {
        super(options.node);

        this.cn = options.cn;
        this.source = options.source;

        this.successHandle = options.successHandle;

        this.im = new Inputmask('99.99.9999', { showMaskOnHover: false });
        this.im.mask('input[type="text"].type-date');

        this.form = this.getRoot() as HTMLFormElement;
        this.loader = this.form.querySelector<HTMLDivElement>(this.cn.setElem('loader').toSelector()) as HTMLDivElement;
        this.formButton = this.form.querySelector<HTMLButtonElement>(this.cn.setElem('button').toSelector()) as HTMLButtonElement;

        this.configPhone = options.configPhone;

        if (! this.form) {
            return;
        }

        this.callbackConnection();
    }

    callbackConnection() {
        this.listenerField();
        this.listenerConfirmField();
        this.handleForm();
    }

    showLoader() {
        this.loader.classList.add('show');
    }

    hideLoader() {
        this.loader.classList.remove('show');
    }

    handleForm() {
        this.form.addEventListener('submit', e => {
            e.preventDefault();

            this.formatFormFieldsToCorrectValue();
            const formData = new FormData(this.form);
            const formValidationResult = this.fieldValidation(formData);

            if (! formValidationResult.valid) {
                formValidationResult.invalidFieldName.forEach(error => {
                    if (error.fieldName === 'phone' && error.isEmpty === false) {
                        // За вывод ошибок для телефона отвечает класс confirmPhone
                        this.configPhone.renderNotConfirmError();
                    } else {
                        this.openFieldError(error);
                    }
                });

                return;
            }

            this.showLoader();

            const formBody = this.prepareFormData(formData);

            Pixel.whenCreateAccount();

            Gate31ApiV2.createAccount(formBody)
                .then(response => {
                    this.hideLoader();

                    if (response?.status === 'Success') {
                        this.successHandle?.();
                    }

                    if (response.error) {
                        const errorCode = response?.error?.cause?.code?.code;
                        const cause = response?.error?.cause;
                        const code = cause ? cause?.code?.code : 0;
                        const message = cause ? cause?.phone?.[0] || cause?.email?.[0] : '';

                        if (message) {
                            this.openRequestError(message);
                            return;
                        }

                        if (code) {
                            this.handleRequestError({
                                code: code || errorCode
                            });
                            return;
                        }
                    }

                    throw response.error;
                })
                .catch(error => {
                    try {
                        faro.api.pushError(error, {
                            context: {
                                apiMethod: 'createAccount',
                                component: 'CreateAccountDesk',
                                jsonError: JSON.stringify(error)
                            }
                        });
                    } catch (sendError) {
                        faro.api.pushError(sendError as Error, {
                            context: {
                                apiMethod: 'createAccount',
                                component: 'CreateAccountDesk'
                            }
                        });
                    }

                    this.hideLoader();

                    this.openRequestError('Что-то пошло не так. Пожалуйста повторите попытку...');
                });
        });
    }

    handleRequestError(error: { code: string }) {
        switch (error.code) {
            case 'INVALID_PHONE':
                this.openRequestError('Укажите корректный номер телефона');
                break;
            case 'INVALID_DATE':
                this.openRequestFieldError('Некорректная дата', 'dateOfBirth');
                break;
            case 'INVALID_EMAIL':
                this.openRequestError('Укажите корректный email');
                break;
            case 'INVALID_USER_NAME':
                this.openRequestError('Необходимо указать имя');
                break;
            case 'INVALID_PASSWORD':
                this.openRequestError('Длина пароля не должна быть меньше 6 символов');
                break;
            case 'ERROR_GRECAPTCHA':
                this.openRequestError('Подтвердите что вы не робот');
                break;
            default:
                this.openRequestError('Что-то пошло не так. Пожалуйста повторите попытку...');
                break;
        }
    }

    openRequestFieldError(message: string, field: string) {
        const node = this.getRoot().querySelector(this.cn.setElem('field-wrap').setMode({ [field]: true }).toSelector()) as HTMLDivElement;
        const nodeErrorMessage = node.querySelector<HTMLElement>(this.cn.setElem('field-error').toSelector());

        if (nodeErrorMessage) {
            nodeErrorMessage.innerHTML = message;
            nodeErrorMessage.classList.add(this.cn.setElem('field-error').setMode({ active: true }).toString());
        }
    }

    openRequestError(message: string) {
        const errorNode = this.getRoot().querySelector(this.cn.setElem('result-error').toSelector()) as HTMLDivElement;

        errorNode.innerHTML = message;
        errorNode?.classList.add(this.cn.setElem('field-error').setMode({ active: true }).toString());

        setInterval(() => {
            errorNode?.classList.remove(this.cn.setElem('field-error').setMode({ active: true }).toString());
        }, 5000);
    }

    listenerField() {
        this.form.addEventListener('input', e => {
            const fieldName = (e.target as HTMLInputElement).getAttribute('name') as string;

            if (fieldName) {
                this.hiddenFieldError(fieldName);
            }
        });
    }

    listenerConfirmField() {
        const confirmField =
        this.getRoot().querySelector<HTMLInputElement>(this.cn.setElem('checkbox').setElem('checkbox-input').toSelector() + '[name="is-confirm"]') as HTMLInputElement;

        confirmField.addEventListener('change', () => {
            if (confirmField.checked) {
                this.unableFormButton();
            } else {
                this.disabledFormButton();
            }
        });
    }

    disabledFormButton() {
        this.formButton.setAttribute('disabled', 'true');
    }

    unableFormButton() {
        this.formButton.removeAttribute('disabled');
    }

    formatFormFieldsToCorrectValue(): void {
        // Iterate over all fields when trimming is needed
        for (const fieldName of [ 'firstName', 'lastName', 'email' ]) {
            const fieldWhenNeedTrimming = this.form.querySelector<HTMLInputElement>(`input[name="${fieldName}"]`);

            if (fieldWhenNeedTrimming) {
                fieldWhenNeedTrimming.value = fieldWhenNeedTrimming.value.trim();
            }
        }
    }

    fieldValidation(formData: FormData): { valid: false; invalidFieldName: Array<FieldError> } | { valid: true } {
        let isValid = true;
        const errorList: Array<FieldError> = [];

        if (! /[0-9][0-9]\.[0-9][0-9]\.[0-9][0-9][0-9][0-9]/.test((formData.get('dateOfBirth') as string)) && (formData.get('dateOfBirth') as string) !== '') {
            isValid = false;
            errorList.push({
                fieldName: 'dateOfBirth',
                isEmpty: false
            });
        }

        if (! formData.get('firstName')) {
            isValid = false;
            errorList.push({
                fieldName: 'firstName',
                isEmpty: true
            });
        }
        if (formData.get('email') && ! validateEmail(formData.get('email'))) {
            isValid = false;
            errorList.push({
                fieldName: 'email',
                isEmpty: false
            });
        }

        if (! formData.get('email')) {
            isValid = false;
            errorList.push({
                fieldName: 'email',
                isEmpty: true
            });
        }

        if (formData.get('password') !== formData.get('password-2')) {
            isValid = false;
            errorList.push({
                fieldName: 'password-2',
                isEmpty: false
            });
        }

        if (! this.configPhone.$phoneField.value && ! this.configPhone.confirmed) {
            errorList.push({
                fieldName: 'phone',
                isEmpty: true
            });
        }

        if (this.configPhone.$phoneField.value && ! this.configPhone.confirmed) {
            isValid = false;
            errorList.push({
                fieldName: 'phone',
                isEmpty: false
            });
        }

        if (isValid === false) {
            return {
                valid: false,
                invalidFieldName: errorList
            };
        }

        return {
            valid: true
        };
    }

    openFieldError(error: FieldError) {
        const node = this.getRoot().querySelector(this.cn.setElem('field-wrap').setMode({ [error.fieldName]: true }).toSelector()) as HTMLDivElement;

        const nodeErrorMessage = node.querySelector<HTMLElement>(this.cn.setElem('field-error').toSelector());

        if (error.isEmpty) {
            node.classList.add(this.cn.setElem('field-wrap').setMode({ empty: true }).toString());
        } else {
            if (nodeErrorMessage) {
                nodeErrorMessage.classList.add(this.cn.setElem('field-error').setMode({ active: true }).toString());
            }
        }
    }

    hiddenFieldError(fieldName: string) {
        const node = this.getRoot().querySelector(this.cn.setElem('field-wrap').setMode({ [fieldName]: true }).toSelector()) as HTMLDivElement;
        const nodeErrorMessage = node.querySelector<HTMLElement>(this.cn.setElem('field-error').toSelector());

        if (node) {
            node.classList.remove(this.cn.setElem('field-wrap').setMode({ empty: true }).toString());
        }

        if (nodeErrorMessage) {
            nodeErrorMessage.classList.remove(this.cn.setElem('field-error').setMode({ active: true }).toString());
        }
    }

    transformDateOfBirth(date: string): string {
        return date ? date.split('.').reverse().join('-') || '' : '';
    }

    prepareFormData(formData: FormData) {
        const paramsFromUrl = new URLSearchParams(window.location.search);
        const shopValue = paramsFromUrl.get('shop') ? decodeURI((paramsFromUrl.get('shop') as string)) : '';

        const opts: {
            grecaptcha?: string;
            isUseCaptcha: boolean;
        } = {
            isUseCaptcha: false
        };

        const phoneField = this.form.querySelector<HTMLInputElement>('[name="phone"]');
        const phone = phoneField ? phoneField.value : null;

        const dateOfBirth = this.transformDateOfBirth(formData.get('dateOfBirth') as string);

        const body = {
            ...opts,
            email: formData.get('email') as string,
            phone: phone as string,
            password: formData.get('password') ? formData.get('password') as string : '',
            dateOfBirth,
            name: {
                firstName: formData.get('firstName') as string,
                lastName: formData.get('lastName') ? formData.get('lastName') as string : ''
            },
            endpoint: {
                shop: shopValue,
                source: this.source
            },
            subscriptions: {
                email: true,
                phone: true
            }
        };

        return body;
    }
}
